export const BEACON_PHASES = [
  { key: "spinup", label: "Beacon spinning up" },
  { key: "fetch.home", label: "Beacon fetching homepage" },
  { key: "fetch.robots", label: "Beacon reading robots.txt" },
  { key: "fetch.sitemap", label: "Beacon parsing sitemap" },
  { key: "detect.stack", label: "Beacon fingerprinting CDN and CMS" },
  { key: "parse.schema", label: "Beacon parsing existing JSON-LD schema" },
  { key: "detect.crawlers", label: "Beacon checking AI crawler policy" },
  { key: "discovery", label: "Beacon mapping every URL" },
  { key: "classify", label: "Beacon classifying URLs" },
  { key: "standardisation", label: "Beacon sampling 3 location pages" },
  { key: "crawl.deep", label: "Beacon crawling site pages" },
  { key: "extract.locations", label: "Beacon extracting per-location data" },
  { key: "extract.anchors", label: "Beacon extracting membership and classes" },
  { key: "score", label: "Beacon scoring AEO readiness" },
  { key: "build.robots", label: "Beacon drafting robots.txt" },
  { key: "build.schema", label: "Beacon writing schema blocks" },
  { key: "build.llms", label: "Beacon composing llms.txt" },
  { key: "build.llmsfull", label: "Beacon composing llms-full.txt" },
  { key: "render.html", label: "Beacon rendering HTML report" },
  { key: "validate", label: "Beacon validating deliverables" },
] as const;

export type BeaconPhaseKey = (typeof BEACON_PHASES)[number]["key"];
export type BeaconPhaseStatus =
  | "pending"
  | "running"
  | "ok"
  | "fail"
  | "skipped";
export type BeaconRunStatus =
  | "idle"
  | "confirming"
  | "queued"
  | "running"
  | "succeeded"
  | "failed";

// Stage grouping reflects *execution order*. The 5-step AEO framework
// appears as narration inside the Scoring stage, where the scoring actually
// happens. Putting framework labels on the earlier probe stages was
// misleading because Beacon runs them in parallel — step 4/5 would finish
// at the same time as step 1/2 and appear to "jump ahead of" the long
// crawl (step 3).
export const BEACON_STAGES = [
  {
    key: "setup",
    label: "Spinning up",
    phaseKeys: ["spinup"],
  },
  {
    key: "inspection",
    label: "Inspecting site",
    phaseKeys: [
      "fetch.home",
      "fetch.robots",
      "fetch.sitemap",
      "detect.stack",
      "parse.schema",
      "detect.crawlers",
    ],
  },
  {
    key: "mapping",
    label: "Mapping every URL",
    phaseKeys: ["discovery", "classify"],
  },
  {
    key: "sampling",
    label: "Sampling 3 location pages",
    phaseKeys: ["standardisation"],
  },
  {
    key: "crawl",
    label: "Deep site crawl",
    phaseKeys: ["crawl.deep"],
  },
  {
    key: "extract",
    label: "Extracting location + anchor data",
    phaseKeys: ["extract.locations", "extract.anchors"],
  },
  {
    key: "scoring",
    label: "Scoring · 5-step AEO framework",
    phaseKeys: ["score"],
  },
  {
    key: "build",
    label: "Building deliverables",
    phaseKeys: [
      "build.robots",
      "build.schema",
      "build.llms",
      "build.llmsfull",
    ],
  },
  {
    key: "finalise",
    label: "Finalising",
    phaseKeys: ["render.html", "validate"],
  },
] as const;

export type BeaconStageKey = (typeof BEACON_STAGES)[number]["key"];
export type BeaconLevel = "L0" | "L1" | "L2" | "L3" | "L4";

export interface BeaconPhaseUiState {
  key: BeaconPhaseKey;
  label: string;
  status: BeaconPhaseStatus;
  detail: string;
  durationMs?: number;
  progress?: { completed: number; total: number };
}

export type BeaconSseEvent =
  | { type: "phase.start"; key: BeaconPhaseKey; label: string; index: number; total: number; at: string }
  | {
      type: "phase.progress";
      key: BeaconPhaseKey;
      detail: string;
      progress?: { completed: number; total: number };
      at: string;
    }
  | { type: "phase.end"; key: BeaconPhaseKey; status: BeaconPhaseStatus; durationMs: number; at: string }
  | { type: "run.log"; level: "info" | "warn"; message: string; at: string }
  | {
      type: "discovery.complete";
      urlCount: number;
      source:
        | "sitemap"
        | "sitemap-index"
        | "wp-rest"
        | "nav-regex"
        | "crawl-fallback";
      at: string;
    }
  | {
      type: "standardisation.complete";
      branch: "A" | "B" | "C";
      sampleUrls: string[];
      rationale: string;
      locationCount: number;
      etaSeconds: number;
      at: string;
    }
  | {
      type: "run.complete";
      runId: string;
      reportSlug: string;
      publicUrl: string;
      score: { total: number; level: BeaconLevel };
      at: string;
    }
  | { type: "run.error"; code: string; message: string; phase?: BeaconPhaseKey; at: string };

/**
 * Mirrors the backend's `BeaconArtefactKind` union (in
 * beacon-standalone/src/services/beacon/types.ts). Adding a new kind
 * here without adding it on the backend (or vice versa) silently 400s
 * downloads — keep them in lockstep.
 */
export type ArtefactKind =
  | "robots"
  | "schema"
  | "gtm"
  | "llmsTxt"
  | "llmsFullTxt"
  | "html"
  | "auditSummaryMd"
  | "sitemapXml";
